import {
  ApiBlueprint,
  configApiRef,
  coreExtensionData,
  createApiRef,
  createExtension,
  createFrontendModule,
  discoveryApiRef,
  oauthRequestApiRef,
  useApi,
} from '@backstage/frontend-plugin-api';
import { ProviderSettingsItem } from '@backstage/plugin-user-settings';
import {
  SignInPageBlueprint,
  type SignInPageProps,
} from '@backstage/plugin-app-react';
import {
  type BackstageIdentityApi,
  type OAuthApi,
  type OpenIdConnectApi,
  type ProfileInfoApi,
  type SessionApi,
} from '@backstage/core-plugin-api';
import { OAuth2 } from '@backstage/core-app-api';
import { SignInPage } from '@backstage/core-components';
import { makeStyles } from '@material-ui/core';
import oneplatformLogo from '../nav/assets/aeven-oneplatform-logo.png';
import { LOGO_ASPECT, LOGO_CREAM, LOGO_PADDING } from '../nav/brand';

const LOGO_HEIGHT = 60;
const LOGO_PAD = Math.round(LOGO_HEIGHT * LOGO_PADDING);
const LOGO_ART = LOGO_HEIGHT - 2 * LOGO_PAD;

const useSignInStyles = makeStyles(theme => ({
  root: {
    position: 'relative',
    height: '100%',
  },
  // The OnePlatform logo centered in the sign-in header band (the sage
  // banner). This is the first screen anyone sees, and what they are signing
  // in to is the platform, not the company - which is the distinction the
  // brand guide draws between this logo and the plain aeven wordmark.
  // Top-anchored rather than centred in a fixed-height bar: the sign-in
  // header is a stock Backstage `Header`, so its height is content-driven
  // (85px with this app title) and hardcoding it here would leave the plate
  // hanging over the edge the moment the title wraps. Anchoring to the top
  // with a fixed inset only assumes the band is taller than the plate.
  logoBar: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    padding: 12,
    pointerEvents: 'none',
    zIndex: 1500,
  },
  // Same cream plate as the sidebar, and for the same reason: the banner is
  // sage and the wordmark in the asset is near black. See modules/nav/brand.
  plate: {
    display: 'inline-flex',
    alignItems: 'center',
    height: LOGO_HEIGHT,
    padding: LOGO_PAD,
    boxSizing: 'border-box',
    borderRadius: theme.shape.borderRadius,
    background: LOGO_CREAM,
  },
  logo: {
    height: LOGO_ART,
    width: LOGO_ART * LOGO_ASPECT,
    display: 'block',
  },
}));

export const oidcAuthApiRef = createApiRef<
  OAuthApi &
    OpenIdConnectApi &
    ProfileInfoApi &
    BackstageIdentityApi &
    SessionApi
>({ id: 'auth.oidc' });

const oidcProvider = {
  id: 'oidc',
  title: 'Zitadel',
  message: 'Sign in using your Zitadel account',
  apiRef: oidcAuthApiRef,
};

function SignInPageWrapper(props: SignInPageProps) {
  const classes = useSignInStyles();
  const configApi = useApi(configApiRef);
  const isDevelopment =
    configApi.getOptionalString('auth.environment') === 'development';

  return (
    <div className={classes.root}>
      <div className={classes.logoBar}>
        <span className={classes.plate}>
          <img
            className={classes.logo}
            src={oneplatformLogo}
            alt="Aeven OnePlatform"
          />
        </span>
      </div>
      {isDevelopment ? (
        <SignInPage {...props} providers={['guest', oidcProvider]} />
      ) : (
        <SignInPage {...props} provider={oidcProvider} />
      )}
    </div>
  );
}

// Adds Zitadel to the Settings > Authentication Providers list so users can
// see and manage their session there (the default list only knows the
// built-in Backstage providers).
const zitadelProviderSettings = createExtension({
  name: 'zitadel-provider-settings',
  attachTo: {
    id: 'sub-page:user-settings/auth-providers',
    input: 'providerSettings',
  },
  output: [coreExtensionData.reactElement],
  factory: () => [
    coreExtensionData.reactElement(
      <ProviderSettingsItem
        title="Zitadel"
        description="Provides authentication towards Zitadel APIs and identities"
        apiRef={oidcAuthApiRef}
        icon={() => null}
      />,
    ),
  ],
});

export const authModule = createFrontendModule({
  pluginId: 'app',
  extensions: [
    zitadelProviderSettings,
    ApiBlueprint.make({
      name: 'oidc',
      params: defineApi =>
        defineApi({
          api: oidcAuthApiRef,
          deps: {
            discoveryApi: discoveryApiRef,
            oauthRequestApi: oauthRequestApiRef,
            configApi: configApiRef,
          },
          factory: ({ discoveryApi, oauthRequestApi, configApi }) =>
            OAuth2.create({
              configApi,
              discoveryApi,
              oauthRequestApi,
              environment: configApi.getOptionalString('auth.environment'),
              provider: {
                id: 'oidc',
                title: 'Zitadel',
                icon: () => null,
              },
              defaultScopes: ['openid', 'profile', 'email'],
            }),
        }),
    }),
    SignInPageBlueprint.make({
      params: {
        loader: async () => SignInPageWrapper,
      },
    }),
  ],
});
